import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet-async';
import { debounce } from 'lodash';
import Faq from '../../components/Faq/Faq';
import BusDetailsList from '../../components/BusDetailsList/BusDetailsList';
import Footer from '../../components/Footer/Footer';
import BreadCrumb from '../../components/BreadCrumb/BreadCrumb';
import PageHeader from '../../components/PageHeader/PageHeader';
import StarRatingBox from '../../components/StarRatingBox/StarRatingBox';
import DataLoaderError from '../../../server/errors/DataLoaderError';
import BusModifySearch from '../../components/BusModifySearch/BusModifySearch';
import ThingsToKnow from '../../components/ThingsToKnow/ThingsToKnow';
import AppContextProvider from '../../AppContext';
import ReturnBusSection from '../../components/ReturnBusSection';
import { RouteComponentProps } from 'react-router-dom';
import '../RouteTemplate/RouteTemplate.css';
import {
  RouteCategoryTemplateDataChecker,
  RouteCategoryData,
} from './RouteCategoryDataTypeChecker';
import { PathReporter } from 'io-ts/lib/PathReporter';
import { trackRouteCategoryTemplateEvent } from '../../utils/omnitureTracking';
import { EnvironmentVariables } from '../../types';
import { getAppEnv } from '../../../config/env';
import AdsComponent from '../../components/AdsComponent';

import OfferDropdown from '../../components/OfferDropdown/OfferDropdown';
import CompactListV1 from '../../components/CompactList/CompactListV1';
import SchemaInformation, {
  SchemaInformationTypes,
} from '../../components/SchemaInformation/SchemaInformation';
import { yieldToMainThread } from '../../utils/scheduler';

type PathParamsType = {
  fromCityCode: string;
  toCityCode: string;
};
type RouteCategoryTemplateProp = RouteComponentProps<PathParamsType> & {
  data: RouteCategoryData;
};
type MousePosType = {
  x: number;
  y: number;
};

const env = getAppEnv();
const { DATA_LOADER_URL } = env.raw as EnvironmentVariables;
const isSSR = typeof window === 'undefined';

const runtimeTypChecker = (data: RouteCategoryData) => {
  const result = RouteCategoryTemplateDataChecker.decode(data);
  if (result._tag !== 'Right') {
    console.log(PathReporter.report(result));
    // ThrowReporter.report(result);
  }
};

const RouteCategoryTemplate = ({ data }: RouteCategoryTemplateProp) => {
  const {
    routeData,
    categoryData,
    nearbyCitiesSource,
    nearbyCitiesDestination,
    seoOfferData,
    schemaInfo,
  } = data;
  const {
    topOperatorsList,
    sourceCityData,
    destinationCityData,
    topBusesFrom,
    returnRouteCategoryUrl,
  } = routeData;
  const { templateData, offers, mySafetyHtmls } = data;
  const { name: sourceCityName } = sourceCityData;
  const { name: destinationCityName } = destinationCityData;
  const { code: sourceCityCode } = sourceCityData;
  const { code: destinationCityCode } = destinationCityData;

  const _mqMobile = useMediaQuery({
    query: '(max-device-width: 768px)',
  });
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  const isMobileDevice = isHydrated ? _mqMobile : false;
  const [collapsableFlag, setcollapsableFlag] = useState(false);
  const [fromCity, setFromCity] = useState(
    sourceCityName && sourceCityCode ? sourceCityName : '',
  );
  const [toCity, setToCity] = useState(
    destinationCityName && destinationCityCode ? destinationCityName : '',
  );
  const [fromCode, setFromCode] = useState(
    sourceCityName && sourceCityCode ? sourceCityCode : '',
  );
  const [toCode, setToCode] = useState(
    destinationCityName && destinationCityCode ? destinationCityCode : '',
  );
  const [widget, setWidget] = useState<null | number>(null);
  const [funnelUrl, setFunnelUrl] = useState<'' | string>('');

  const date = new Date();
  date.setDate(date.getDate() + 1);

  const onBusClicked = (funnelUrl?: string) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        onFromChange(sourceCityName, sourceCityCode);
        onToChange(destinationCityName, destinationCityCode);
        funnelUrl && setFunnelUrl(funnelUrl);
      });
    });
  };
  const onFromChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setFromCity(city);
        setFromCode(code);
      });
    });
    trackRouteCategoryTemplateEvent(`Bus_SEO_Route_Category_From_Select`);
  };

  const onToChange = (city, code) => {
    yieldToMainThread(() => {
      requestAnimationFrame(() => {
        setToCity(city);
        setToCode(code);
      });
    });
    trackRouteCategoryTemplateEvent(`Bus_SEO_Route_Category_To_Select`);
  };

  const getListingUrl = (): string => {
    return isMobileDevice
      ? `https://www.makemytrip.com/mbus/rnw/busListingPage?from=${fromCity}&from_code=${fromCode}&to=${toCity}&to_code=${toCode}&departure=travelDate&sort=Rating&`
      : `https://www.makemytrip.com/bus/search/${fromCity}/${toCity}/travelDate?from_city=${fromCode}&to_city=${toCode}&`;
  };

  const [mousePos, setMousePos] = useState({} as MousePosType);
  const [openOffersDropdown, setOpenOffersDropdown] = useState(false);
  const [hasDropdownOpened, setHasDropdownOpened] = useState(false);

  const srcBoardingPointsBlockData = {
    mainData: sourceCityData?.cityBoardingPointsList,
    titlePrefix: 'Boarding Points',
    titleSuffix: ` in ${sourceCityName}`,
  };

  const srcOtherRoutesFromBlockData = {
    mainData: sourceCityData?.otherRoutesFrom,
    titlePrefix: 'Other routes',
    titleSuffix: ` from ${sourceCityName}`,
  };

  const srcOtherRoutesToBlockData = {
    mainData: sourceCityData?.otherRoutesTo,
    titlePrefix: 'Other routes',
    titleSuffix: ` to ${sourceCityName}`,
  };

  const otherRoutesCategoriesBlockData = {
    mainData: sourceCityData?.otherRouteCategories,
    titlePrefix: `Other ${categoryData?.categoryName} buses`,
    titleSuffix: ` from ${sourceCityName}`,
  };

  const nearbyCitiesBlockData = {
    mainData: nearbyCitiesSource,
    titlePrefix: `Popular Cities near `,
    titleSuffix: sourceCityName,
  };

  const sourceInterlinkingBlockData = [
    srcBoardingPointsBlockData,
    srcOtherRoutesFromBlockData,
    srcOtherRoutesToBlockData,
    otherRoutesCategoriesBlockData,
    nearbyCitiesBlockData,
  ];

  const desDroppingPointsBlockData = {
    mainData: destinationCityData?.cityDropPointsList,
    titlePrefix: 'Dropping Points',
    titleSuffix: ` in ${destinationCityName}`,
  };

  const desOtherRoutesFromBlockData = {
    mainData: destinationCityData?.otherRoutesFrom,
    titlePrefix: 'Other routes',
    titleSuffix: ` from ${destinationCityName}`,
  };

  const desOtherRoutesToBlockData = {
    mainData: destinationCityData?.otherRoutesTo,
    titlePrefix: 'Other routes',
    titleSuffix: ` to ${destinationCityName}`,
  };

  const desOtherRouteCategoriesBlockData = {
    mainData: destinationCityData?.otherRouteCategories,
    titlePrefix: `Other ${categoryData?.categoryName} buses`,
    titleSuffix: ` from ${destinationCityName}`,
  };

  const destinationPopularCitiesBlockData = {
    mainData: nearbyCitiesDestination,
    titlePrefix: `Popular Cities near `,
    titleSuffix: destinationCityName,
  };

  const destinationInterlinkingBlockData = [
    desDroppingPointsBlockData,
    desOtherRoutesFromBlockData,
    desOtherRoutesToBlockData,
    desOtherRouteCategoriesBlockData,
    destinationPopularCitiesBlockData,
  ];

  useEffect(() => {
    if (seoOfferData?.length > 0) {
      const handleMouseMove = debounce((event) => {
        if (!hasDropdownOpened) {
          setMousePos({ x: event.clientX, y: event.clientY });
        }
      }, 250);

      window.addEventListener('mousemove', handleMouseMove);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, []);

  useEffect(() => {
    if (
      Object.keys(mousePos).length > 0 &&
      mousePos.y <= 20 &&
      !hasDropdownOpened
    ) {
      setOpenOffersDropdown(true);
      setHasDropdownOpened(true);
    }
  }, [mousePos]);

  useEffect(() => {
    const seeGuildLinesElement = document.getElementById('SeeGuidelines');
    seeGuildLinesElement &&
      seeGuildLinesElement.addEventListener('click', () => {
        trackRouteCategoryTemplateEvent(
          `Bus_SEO_RouteCategoryTemplate_MySafety_See_GuideLines_Click`,
        );
      });

    const learnMoreElement = document.getElementById('LearnMore');
    learnMoreElement &&
      learnMoreElement.addEventListener('click', () => {
        trackRouteCategoryTemplateEvent(
          `Bus_SEO_RouteCategoryTemplate_MySafety_Learn_More_Click`,
        );
      });
  }, []);

  return (
    <AppContextProvider
      omniUrlArgs={'source=SEO_Route_Category_Template'}
      trackTemplateEvent={trackRouteCategoryTemplateEvent}
    >
      {schemaInfo && (
        <SchemaInformation
          schemaInformation={schemaInfo as SchemaInformationTypes}
          templateData={templateData}
        />
      )}
      <div className={`busSeoPage`}>
        <Helmet>
          <title>{templateData.metaTitle}</title>
          <meta name="description" content={templateData.metaDescription} />
          <meta name="keywords" content={templateData.metaKeywords} />
          <link rel="canonical" href={templateData.sampleUrl} />
        </Helmet>
        <div className={`desktopShow makeSticky`}>
          <BusModifySearch
            funnelUrl={funnelUrl}
            category={categoryData.categoryName}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={false}
            demoLink={true}
          />
        </div>
        <div className={`mobileShow makeSticky`}>
          <BusModifySearch
            funnelUrl={funnelUrl}
            category={categoryData.categoryName}
            sourceCityName={sourceCityName}
            destinationCityName={destinationCityName}
            widget={widget}
            setWidget={setWidget}
            onFromChange={onFromChange}
            onToChange={onToChange}
            fromCity={fromCity}
            toCity={toCity}
            fromCode={fromCode}
            toCode={toCode}
            setcollapsableFlag={setcollapsableFlag}
            collapsableFlag={collapsableFlag}
            isMobileDevice={true}
            demoLink={true}
          />
        </div>

        <main className="container">
          <BreadCrumb
            breadcrumb={templateData.breadcrumb}
            type={`_Route_Category`}
          />
          <PageHeader title={templateData.h1Title} />
          {
            <AdsComponent
              template={'Route_Category'}
              placedAt={'top'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: null },
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom20 adsLayout',
                style: { marginTop: null },
              }}
            />
          }
          <br style={{ display: 'none' }} />
          {topBusesFrom &&
            topBusesFrom.categorizedBusGroups &&
            topBusesFrom.categorizedBusGroups.length > 0 && (
              <>
                <BusDetailsList
                  noOfListings={
                    templateData.noOfListings ? templateData.noOfListings : 10
                  }
                  titlePrefix={`Top ${categoryData.categoryName} Buses `}
                  type={'_Route_Category'}
                  busGroups={topBusesFrom.categorizedBusGroups}
                  route={`from ${sourceCityName} to ${destinationCityName}`}
                  onBusClicked={onBusClicked}
                  setcollapsableFlag={setcollapsableFlag}
                  setWidget={setWidget}
                  isMobileDevice={isMobileDevice}
                  fromCity={fromCity}
                  toCity={toCity}
                  fromCode={fromCode}
                  toCode={toCode}
                  selectedDay={date}
                />
                <br />
              </>
            )}
          {topBusesFrom &&
            topBusesFrom.otherBusGroups &&
            topBusesFrom.otherBusGroups.length > 0 && (
              <>
                <BusDetailsList
                  noOfListings={
                    templateData.noOfListings ? templateData.noOfListings : 10
                  }
                  titlePrefix={`Other Buses `}
                  type={'_Route_Category'}
                  busGroups={topBusesFrom.otherBusGroups}
                  route={`from ${sourceCityName} to ${destinationCityName}`}
                  onBusClicked={onBusClicked}
                  setcollapsableFlag={setcollapsableFlag}
                  setWidget={setWidget}
                  isMobileDevice={isMobileDevice}
                  fromCity={fromCity}
                  toCity={toCity}
                  fromCode={fromCode}
                  toCode={toCode}
                  selectedDay={date}
                />
                <br />
              </>
            )}
          {returnRouteCategoryUrl && (
            <ReturnBusSection
              type="Route_Category"
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              categoryName={categoryData.categoryName}
              url={returnRouteCategoryUrl}
            />
          )}
          {
            <AdsComponent
              template={'Route_Category'}
              placedAt={'middle'}
              isMobileDevice={isMobileDevice}
              desktopAdConfig={{
                position: templateData.bannerDesktop
                  ? templateData.bannerDesktop
                  : 'none',
                class: 'desktopShow appendBottom20 adsLayout',
                style: { marginTop: '-20px' },
              }}
              mobileAdConfig={{
                position: templateData.bannerMobile
                  ? templateData.bannerMobile
                  : 'none',
                class: 'mobileShow appendBottom10 adsLayout',
                style: { marginTop: '-5px' },
              }}
            />
          }
          {topOperatorsList && topOperatorsList.length > 0 && (
            <section>
              <div
                style={{ display: 'none' }} // disabled in SSR HTML
                className="starRatngWrap__top--mob paddingR16"
              >
                <p className="font22 latoLight appendBottom2">Top Operators</p>
                <p className="font16 latoBold lineHeight20 blackText appendBottom5">
                  {`from ${sourceCityName} to ${destinationCityName}`}
                </p>
              </div>
              <div className="starRatngWrap__top--desk">
                <p>
                  {`Top Operators for ${sourceCityName} to ${destinationCityName} buses`}
                </p>
              </div>
              <StarRatingBox topOperatorsList={topOperatorsList} />
            </section>
          )}
          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {sourceInterlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>
          {sourceCityData.description && (
            <section>
              <ThingsToKnow
                text={sourceCityData.description}
                title={`Things To Know About`}
                cityName={sourceCityName}
              />
            </section>
          )}
          <div className={`busSeoWrap__Details makeFlex visibilityAuto`}>
            {destinationInterlinkingBlockData.map((item, idx) => {
              const { mainData, titlePrefix, titleSuffix } = item;
              if (!mainData || mainData?.length < 1) {
                return;
              } else {
                return (
                  <CompactListV1
                    blockData={mainData}
                    titlePrefix={titlePrefix}
                    titleSuffix={titleSuffix}
                    key={idx}
                  />
                );
              }
            })}
          </div>
          {destinationCityData.description && (
            <section>
              <ThingsToKnow
                text={destinationCityData.description}
                title={`Things To Know About`}
                cityName={destinationCityName}
              />
            </section>
          )}

          {templateData && templateData.faq && templateData.faq.length > 0 && (
            <section className="visibilityAuto">
              <Faq
                data={templateData.faq}
                getListingUrl={getListingUrl}
                isMobileDevice={isMobileDevice}
                pageUrl={templateData.sampleUrl}
                type={'_Route_Category'}
                FaqTitle={`${sourceCityName} to ${destinationCityName} ${categoryData.categoryName} Bus Booking - FAQ`}
              />
            </section>
          )}
        </main>

        {!isMobileDevice &&
          openOffersDropdown &&
          hasDropdownOpened &&
          seoOfferData.length > 0 && (
            <OfferDropdown
              seoOfferData={seoOfferData}
              setOpenOffersDropdown={setOpenOffersDropdown}
            />
          )}
        {templateData && <Footer template={templateData} />}
        <div
          className="blackOverlay"
          onClick={() => {
            setcollapsableFlag(true);
          }}
        ></div>
      </div>
    </AppContextProvider>
  );
};

RouteCategoryTemplate.fetchData = async (_, match, urlObj, gRPCClient) => {
  const {
    params: { routeCategoryId },
  } = match;
  const url = `${DATA_LOADER_URL}/api/routeCategoryTemplateData/${routeCategoryId}?${urlObj.query}`;
  let responseData;

  const response = await fetch(url);
  const textResponse = await response.text();
  try {
    responseData = JSON.parse(textResponse);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new Error('JSON parse exception: ' + textResponse);
  }
  if (responseData.status !== 'SUCCESS') {
    // eslint-disable-next-line no-console
    console.error('url', url);
    throw new DataLoaderError(
      `Unable to fetch route category details of ${routeCategoryId}`,
      responseData.error,
    );
  }
  return responseData;
  // return await gRPCClient.getBusCityTemplateDate(cityCode, 'dynamic');
};
export default RouteCategoryTemplate;
